import { LocalStorageService } from 'src/app/utils/LocalStorageService';

export interface CommunicationLogPayloadInput {
  lead_id: number | string;
  communication_type: string;
  note?: string;
  status?: string;
  date_of_communication?: string;
  time_of_communication?: string;
  email_extra_details?: Record<string, unknown>;
  call_extra_details?: Record<string, unknown>;
}

export interface CommunicationLogPayload {
  lead_id: number | string;
  client_id: unknown;
  center_id: unknown;
  user_id: unknown;
  communication_type: string;
  note: string;
  date_of_communication: string;
  status: string;
  time_of_communication?: string;
  email_extra_details?: Record<string, unknown>;
  call_extra_details?: Record<string, unknown>;
}

export function extractCommunicationLogId(res: unknown): number | null {
  if (!res || typeof res !== 'object') {
    return null;
  }
  const record = res as Record<string, unknown>;
  const dataValues = record['dataValues'] as Record<string, unknown> | undefined;
  const rawId = record['id'] ?? dataValues?.['id'];
  if (rawId == null || rawId === '') {
    return null;
  }
  const id = Number(rawId);
  return Number.isFinite(id) ? id : null;
}

export function buildCommunicationLogPayload(
  localStorage: LocalStorageService,
  input: CommunicationLogPayloadInput
): CommunicationLogPayload {
  const payload: CommunicationLogPayload = {
    lead_id: input.lead_id,
    client_id: localStorage.getClientId(),
    center_id: localStorage.getCenterId(),
    user_id: localStorage.getUserId(),
    communication_type: input.communication_type,
    note: input.note ?? '',
    date_of_communication:
      input.date_of_communication ?? new Date().toISOString().split('T')[0],
    status: input.status ?? 'Sent',
  };

  if (input.time_of_communication) {
    payload.time_of_communication = input.time_of_communication;
  }
  if (input.email_extra_details) {
    payload.email_extra_details = input.email_extra_details;
  }
  if (input.call_extra_details) {
    payload.call_extra_details = input.call_extra_details;
  }

  return payload;
}

export interface CommCardData {
  typeLabel: string;
  subject?: string;
  date?: string;
  time?: string;
  message?: string;
  status?: string;
  duration?: string;
}

function formatCommLogDate(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function formatCommLogTime(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }
  const raw = String(value).trim();
  if (/^\d{1,2}:\d{2}/.test(raw)) {
    return raw.slice(0, 5);
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return raw;
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** User-friendly labels for communication timeline cards (email / WhatsApp / call). */
export function buildCommCardData(row: Record<string, unknown>): CommCardData {
  const type = String(row['communication_type'] || '').toLowerCase();
  const typeLabel = type
    ? type.charAt(0).toUpperCase() + type.slice(1)
    : 'Communication';

  const extra = row['email_extra_details'];
  const subject =
    extra && typeof extra === 'object' && (extra as Record<string, unknown>)['subject']
      ? String((extra as Record<string, unknown>)['subject']).trim()
      : '';

  const note = row['note'] ? String(row['note']).trim() : '';
  const status = row['status'] ? String(row['status']).trim() : '';

  const date =
    formatCommLogDate(row['date_of_communication']) ||
    formatCommLogDate(row['createdAt']);

  let time = formatCommLogTime(row['time_of_communication']);
  if (!time && row['createdAt']) {
    time = formatCommLogTime(row['createdAt']);
  }

  const callExtra = row['call_extra_details'];
  let duration = '';
  if (callExtra && typeof callExtra === 'object') {
    const ce = callExtra as Record<string, unknown>;
    const raw = ce['callDuration'] ?? ce['duration'];
    if (raw != null && raw !== '') {
      duration = `${raw} sec`;
    }
  }

  const card: CommCardData = {
    typeLabel,
    date: date || undefined,
    message: note || undefined,
    status: status || undefined,
  };

  if (time) {
    card.time = time;
  }
  if (type === 'email' && subject) {
    card.subject = subject;
  }
  if (type === 'call' && duration) {
    card.duration = duration;
  }

  return card;
}
